import { WageFrequency } from "@api/shared-runtime/enums";
import type { ActiveOrganizationWageSet } from "@/features/organization-wage-sets/hooks/use-active-organization-wage-sets";
import { OfferWageSource, type JobOfferData } from "./job-offer-data";

/** Wage fields to send when the offer holds its own wages. */
export function customWages(data: JobOfferData, isPerShift: boolean) {
  return {
    regularWage: data.regularWage,
    regularOvertimeWage: isPerShift
      ? undefined
      : data.regularOvertimeWage || undefined,
    weekendWage: data.weekendWage || undefined,
    weekendOvertimeWage: isPerShift
      ? undefined
      : data.weekendOvertimeWage || undefined,
    onCallWage: data.onCallWage || undefined,
    onCallOvertimeWage: isPerShift
      ? undefined
      : data.onCallOvertimeWage || undefined,
    onCallCallbackWage: data.onCallCallbackWage || undefined,
    onCallCallbackOvertimeWage: isPerShift
      ? undefined
      : data.onCallCallbackOvertimeWage || undefined,
    holidayWage: data.holidayWage || undefined,
    holidayOvertimeWage: isPerShift
      ? undefined
      : data.holidayOvertimeWage || undefined,
    wageFrequencyId: data.wageFrequencyId,
  };
}

/**
 * The live preview shows the offer as providers will see it: with a wage set
 * chosen, the wage set's current wages stand in for the hidden wage fields.
 * Providers see only the wages, never the wage set's name, which may be internal.
 */
export function withOrganizationWageSetPreview({
  formData,
  organizationWageSets,
}: {
  formData: JobOfferData;
  organizationWageSets: ActiveOrganizationWageSet[] | undefined;
}): JobOfferData {
  const orgWageSet = organizationWageSets?.find(
    (candidate) =>
      candidate.organizationWageSetId === formData.organizationWageSetId,
  );
  if (
    formData.wageSource !== OfferWageSource.OrganizationWageSet ||
    !orgWageSet
  ) {
    return formData;
  }
  const { wages } = orgWageSet;
  const isOneTime = wages.wageFrequencyId === WageFrequency.OneTime;
  return {
    ...formData,
    regularWage: wages.regularWage,
    regularOvertimeWage: wages.regularOvertimeWage,
    weekendWage: wages.weekendWage,
    weekendOvertimeWage: wages.weekendOvertimeWage,
    onCallWage: wages.onCallWage,
    onCallOvertimeWage: wages.onCallOvertimeWage,
    onCallCallbackWage: wages.onCallCallbackWage,
    onCallCallbackOvertimeWage: wages.onCallCallbackOvertimeWage,
    holidayWage: wages.holidayWage,
    holidayOvertimeWage: wages.holidayOvertimeWage,
    wageFrequencyId: isOneTime ? WageFrequency.OneTime : WageFrequency.Hourly,
  };
}
